import { useState, useEffect, useCallback, useRef } from 'react';
import { isPlayerPresente } from '@/shared/lib/telaStatus';
import { jukeboxRepository } from '../../data/jukeboxRepository';
import { useJukeboxBroadcast } from './useJukeboxBroadcast';
import { configuracoesService } from '@/shared/services/configuracoesService';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { usePresencaTela } from './usePresencaTela';
import { usePlaylistItens } from './usePlaylistItens';
import { useFilaCooldown } from './useFilaCooldown';
import { usePedidoFlow } from './usePedidoFlow';
import { useVibeQuiz } from './useVibeQuiz';
import { useTemasSazonais } from './useTemasSazonais';
import {
  validarPedido,
  formatarRestante,
} from '../../domain/usecases/validarPedido';
import { playlistIdMudou, type TelaPlaylistPatch } from '../../domain/usecases/trocaPlaylistTela';
import type { FilaJukebox, MusicaAtualBroadcast, PedidoIniciadoBroadcast } from '../../domain/entities/FilaJukebox';
import type { Tela } from '@/shared/models/tela.model';
import type { PlaylistItem } from '@/shared/models/playlist.model';
import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';
import { deveReconectarJukebox, type EventoReconexaoJukebox } from './reconexaoJukebox';

export interface JukeboxState {
  tela:              Tela | null;
  tvOnline:          boolean;
  sincronizando:     boolean;
  items:             PlaylistItem[];
  filaAtiva:         FilaJukebox[];
  cooldowns:         ReadonlyMap<string, number>;
  loading:           boolean;
  pedindoId:         string | null;
  toast:             string | null;
  pedidoConfirmado:  { item: PlaylistItem; posicao: number } | null;
  showPopup:         boolean;
  agora:             number;
  musicaAtual:       MusicaAtualBroadcast | null;
  pedidoIniciado:    PedidoIniciadoBroadcast | null;
  sugestaoAprovada:  { titulo: string; thumbnail: string } | null;
  sugestaoRecusada:  { titulo: string; thumbnail: string } | null;
  pdvToast:          string | null;
  /** UUID de sessão do próprio cliente. Usado para identificar
   *  visualmente seus pedidos na fila e filtrar notificações. */
  meuSessionId:      string;
  handlePedir:          (item: PlaylistItem) => Promise<void>;
  refresh:              () => Promise<void>;
  dismissToast:         () => void;
  dismissConfirmado:    () => void;
  dismissPopup:         () => void;
  batalhaVibesAtiva:     boolean;
  temaDiaNamorados:      boolean;
  temaFestaJunina:       boolean;
  temaCopa:              boolean;
  pendingVibeItem:       PlaylistItem | null;
  handlePedirComVibe:    (vibe: 'romance' | 'sofrencia') => Promise<void>;
  cancelarVibeSelection: () => void;
  meuTime:               'romance' | 'sofrencia' | null;
}

export interface JukeboxClienteSnapshot {
  token: string;
}

/**
 * useJukebox — composer. Orquestra os sub-hooks por concern (presença,
 * playlist, fila/cooldown, pedido, vibe-quiz, temas sazonais) e possui as
 * duas peças genuinamente cross-concern que nenhum sub-hook sozinho pode
 * dono: o fetch inicial (`init`, toca tela+playlist+fila+cooldown+config de
 * uma vez), a troca de playlist via `db_change` no `canal_tela_` (mexe em
 * tela, items e fila juntos) e o `visibilitychange` (ordem de reconnect).
 * `handlePedir` também mora aqui — decide entre validar fila/cooldown,
 * abrir o gate da Batalha de Vibes ou inserir.
 */
export function useJukebox(
  telaId: string | undefined,
  isAuthenticated = false,
  cliente?: JukeboxClienteSnapshot | null,
): JukeboxState {
  const [loading, setLoading] = useState(true);
  const [musicaAtualInicial, setMusicaAtualInicial] = useState<MusicaAtualBroadcast | null>(null);

  const sessionIdRef = useRef<string>('');
  useEffect(() => {
    if (!telaId) return;
    sessionIdRef.current = obterOuCriarSessionId(telaId);
  }, [telaId]);

  const { tela, setTela, telaRef, tvOnline, setTvOnline, sincronizando, setSincronizando } = usePresencaTela();
  const {
    items, setItems, handleReloadPlaylist,
    criarCanalPlaylist, destruirCanalPlaylist,
    iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens,
  } = usePlaylistItens(telaId, tela?.playlist_id);

  const {
    filaAtiva, cooldowns, setCooldowns, agora,
    criarCanalFila, destruirCanalFila, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch,
  } = useFilaCooldown(telaId);

  const ultimaAtividadePlayerRef = useRef<number | null>(null);
  const playlistTrocaGeracaoRef = useRef(0);

  const onTelaAtualizada = useCallback((patch: TelaPlaylistPatch) => {
    if (!telaId) return;
    const anteriorId = telaRef.current?.playlist_id ?? null;
    setTela(prev => prev ? { ...prev, playlist_id: patch.playlist_id, ultima_conexao: patch.ultima_conexao ?? prev.ultima_conexao } : prev);
    setTvOnline(isPlayerPresente(patch.ultima_conexao ?? telaRef.current?.ultima_conexao, ultimaAtividadePlayerRef.current));

    if (!playlistIdMudou(anteriorId, patch.playlist_id)) return;

    const geracao = ++playlistTrocaGeracaoRef.current;
    const novaPlayId = patch.playlist_id;
    if (!novaPlayId) {
      setItems([]);
      const token = iniciarJanelaFetch();
      void jukeboxRepository.limparFilaForaDaPlaylist(telaId)
        .then(() => jukeboxRepository.listarAtivos(telaId))
        .then(fila => {
          if (geracao === playlistTrocaGeracaoRef.current) aplicarSnapshotFila(fila, token);
          else cancelarJanelaFetch(token);
        })
        .catch(err => { cancelarJanelaFetch(token); console.warn('[useJukebox] troca playlist vazia:', err); });
      return;
    }

    const token = iniciarJanelaFetch();
    const tokenItens = iniciarJanelaItens();
    void Promise.all([
      jukeboxRepository.listarPlaylistItens(novaPlayId, telaId),
      jukeboxRepository.limparFilaForaDaPlaylist(telaId).then(() => jukeboxRepository.listarAtivos(telaId)),
    ])
      .then(([itens, fila]) => {
        if (geracao !== playlistTrocaGeracaoRef.current) {
          cancelarJanelaFetch(token);
          cancelarJanelaItens(tokenItens);
          return;
        }
        aplicarSnapshotItens(itens, tokenItens);
        aplicarSnapshotFila(fila, token);
      })
      .catch(err => {
        cancelarJanelaFetch(token);
        cancelarJanelaItens(tokenItens);
        console.warn('[useJukebox] troca playlist:', err);
      });
  }, [telaId, setTela, setTvOnline, setItems, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch, iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens, telaRef]);

  const {
    musicaAtual, pedidoIniciado, sugestaoAprovada, sugestaoRecusada, pdvToast, ultimaAtividadePlayer,
    batalhaVibesAtiva: batalhaVibesBroadcast,
    temaDiaNamorados:  temaDiaNamoradosBroadcast,
    temaFestaJunina:   temaFestaJuninaBroadcast,
    temaCopa:          temaCopaBroadcast,
    criarCanal: criarCanalBroadcast,
    destruirCanal: destruirCanalBroadcast,
  } = useJukeboxBroadcast(
    telaId,
    musicaAtualInicial,
    handleReloadPlaylist,
    sessionIdRef.current,
    onTelaAtualizada,
  );

  const {
    temaDiaNamorados, temaFestaJunina, temaCopa,
    setTemaDiaNamoradosDb, setTemaFestaJuninaDb, setTemaCopaDb,
  } = useTemasSazonais(temaDiaNamoradosBroadcast, temaFestaJuninaBroadcast, temaCopaBroadcast);

  // Broadcast tem prioridade sobre o valor carregado do banco.
  useEffect(() => {
    if (musicaAtual) setSincronizando(false);
  }, [musicaAtual, setSincronizando]);

  // Só o setter: reler ultimaAtividadePlayer do contexto reentraria neste efeito.
  const { registrarAtividadePlayer } = useJukeboxCliente();

  useEffect(() => {
    ultimaAtividadePlayerRef.current = ultimaAtividadePlayer;
    if (ultimaAtividadePlayer !== null) registrarAtividadePlayer();
  }, [ultimaAtividadePlayer, registrarAtividadePlayer]);

  // Presença: ultima_conexao (poll de 15 s) OU broadcast recente no canal da tela.
  useEffect(() => {
    setTvOnline(isPlayerPresente(telaRef.current?.ultima_conexao, ultimaAtividadePlayer));
  }, [ultimaAtividadePlayer, setTvOnline, telaRef]);

  // Timer de 10s: só rebaixa offline quando nenhum dos dois sinais confirma presença.
  useEffect(() => {
    if (!telaId) return;
    const id = setInterval(() => {
      const presente = isPlayerPresente(telaRef.current?.ultima_conexao, ultimaAtividadePlayerRef.current);
      if (!presente) setTvOnline(false);
    }, 10_000);
    return () => clearInterval(id);
  }, [telaId, setTvOnline, telaRef]);

  // db_change de telas não sai em update só de heartbeat — o poll mantém
  // ultima_conexao fresco para o timer de 10 s. Não abre canal novo.
  useEffect(() => {
    if (!telaId) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void jukeboxRepository.buscarTela(telaId).then(tela => {
        setTela(prev => prev ? { ...prev, ultima_conexao: tela.ultima_conexao } : prev);
      }).catch(() => {});
    }, 15_000);
    return () => clearInterval(id);
  }, [telaId, setTela]);

  const initRef = useRef<() => Promise<void>>(async () => {});
  const hidratouMusicaAtualRef = useRef(false);
  const sincronizandoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (sincronizandoTimerRef.current) clearTimeout(sincronizandoTimerRef.current);
  }, []);

  const {
    pedindoId, toast, setToast, pedidoConfirmado, showPopup,
    inserirPedido, dismissToast, dismissConfirmado, dismissPopup,
  } = usePedidoFlow(telaId, tela, isAuthenticated, cliente, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch, pedidoIniciado, sessionIdRef.current);

  const {
    batalhaVibesAtiva, setBatalhaVibesDb, pendingVibeItem, setPendingVibeItem,
    handlePedirComVibe, cancelarVibeSelection, meuTime,
  } = useVibeQuiz(batalhaVibesBroadcast, inserirPedido);

  const handlePedir = useCallback(async (item: PlaylistItem) => {
    if (!telaId || !tela || pedindoId) return;

    if (!tvOnline) {
      setToast('A TV precisa estar online para fazer pedidos.');
      return;
    }

    const resultado = validarPedido(item.video_id, filaAtiva, cooldowns, sessionIdRef.current, isAuthenticated);
    if (!resultado.valido) {
      if (resultado.motivo === 'ja_na_fila') {
        setToast('Esta música já está na fila!');
      } else if (resultado.motivo === 'limite_sessao') {
        const limite = isAuthenticated ? 5 : 3;
        setToast(`Você já tem ${limite} músicas na fila. Aguarde uma tocar para pedir mais.`);
      } else {
        setToast(`Aguarde ${formatarRestante(resultado.cooldownRestanteMs!)} para pedir esta música novamente.`);
      }
      return;
    }

    navigator.vibrate?.(80);

    if (batalhaVibesAtiva && !meuTime) {
      setPendingVibeItem(item);
      return;
    }

    await inserirPedido(item, batalhaVibesAtiva && meuTime ? meuTime : 'neutro');
  }, [telaId, tela, pedindoId, tvOnline, filaAtiva, cooldowns, isAuthenticated, batalhaVibesAtiva, meuTime, inserirPedido, setToast, setPendingVibeItem]);

  // ── Fetch inicial: tela + config (temas/vibes) + playlist/fila/cooldown ──
  useEffect(() => {
    if (!telaId) return;
    let ativo = true;

    async function init() {
      const tokenFila = iniciarJanelaFetch();
      const tokenItens = iniciarJanelaItens();
      let filaAplicada = false;
      let itensAplicados = false;
      try {
        const telaData = await jukeboxRepository.buscarTela(telaId!);
        setTela(telaData);
        setTvOnline(isPlayerPresente(telaData.ultima_conexao, ultimaAtividadePlayerRef.current));

        const cfg = await configuracoesService.buscar(telaData.id).catch(() => null);
        setBatalhaVibesDb(cfg?.config_batalha_vibes ?? false);
        setTemaDiaNamoradosDb(cfg?.config_tema_dia_namorados ?? false);
        setTemaFestaJuninaDb(cfg?.config_tema_festa_junina   ?? false);
        setTemaCopaDb(cfg?.config_tema_copa                  ?? false);

        if (telaData.playlist_id) {
          const seedMusica = !hidratouMusicaAtualRef.current;
          const [itemsData, filaData, tocandoAgora, cooldownStatus] = await Promise.all([
            jukeboxRepository.listarPlaylistItens(telaData.playlist_id, telaId!),
            jukeboxRepository.listarAtivos(telaId!),
            seedMusica
              ? jukeboxRepository.buscarTocandoAgora(telaId!)
              : Promise.resolve(null),
            jukeboxRepository.buscarCooldownStatus(telaId!),
          ]);

          aplicarSnapshotItens(itemsData, tokenItens);
          itensAplicados = true;
          aplicarSnapshotFila(filaData, tokenFila);
          filaAplicada = true;

          const agoraTs = Date.now();
          const mapa = new Map<string, number>();
          for (const p of cooldownStatus) {
            if (p.restante_ms > 0) mapa.set(p.youtube_video_id, agoraTs + p.restante_ms);
          }
          setCooldowns(mapa);

          if (seedMusica) {
            hidratouMusicaAtualRef.current = true;
            if (tocandoAgora) {
              setMusicaAtualInicial({
                youtube_video_id: tocandoAgora.youtube_video_id,
                titulo:           tocandoAgora.titulo,
                thumbnail:        tocandoAgora.thumbnail,
                duracao_segundos: null,
                iniciada_em:      undefined,
              });
            }
          }
        }

        // Now playing após o primeiro load vem só do Player
        // (`solicitar_musica_atual` no SUBSCRIBED do broadcast).
        if (isPlayerPresente(telaData.ultima_conexao, ultimaAtividadePlayerRef.current)) {
          if (!ativo) return;
          setSincronizando(true);
          if (sincronizandoTimerRef.current) clearTimeout(sincronizandoTimerRef.current);
          sincronizandoTimerRef.current = setTimeout(() => setSincronizando(false), 5_000);
        }
      } catch (error) {
        console.error('[useJukebox] Erro ao carregar:', error);
        setToast('Não foi possível carregar o jukebox. Puxe para atualizar.');
      } finally {
        // Erro ou tela sem playlist: a janela não pode ficar aberta acumulando.
        if (!filaAplicada) cancelarJanelaFetch(tokenFila);
        if (!itensAplicados) cancelarJanelaItens(tokenItens);
        setLoading(false);
      }
    }

    initRef.current = init;
    init();
    return () => {
      ativo = false;
      if (sincronizandoTimerRef.current) {
        clearTimeout(sincronizandoTimerRef.current);
        sincronizandoTimerRef.current = null;
      }
    };
  }, [telaId, setTela, setTvOnline, setBatalhaVibesDb, setTemaDiaNamoradosDb, setTemaFestaJuninaDb, setTemaCopaDb, aplicarSnapshotFila, iniciarJanelaFetch, cancelarJanelaFetch, iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens, setCooldowns, setSincronizando, setToast]);

  // ── Active Sync Backup: reconcilia fila contra o banco a cada 3 min ──────
  // Mesma mitigação do Player (`useJukeboxQueue.reconciliarFila`) para gap
  // de eventos do Realtime — sem isso, um UPDATE→concluido perdido deixa o
  // item preso na fila até o próximo reconnect (visibilitychange/pageshow).
  useEffect(() => {
    if (!telaId) return;
    const ACTIVE_SYNC_MS = 3 * 60 * 1_000;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const token = iniciarJanelaFetch();
      jukeboxRepository.listarAtivos(telaId)
        .then(fila => aplicarSnapshotFila(fila, token))
        .catch(err => {
          cancelarJanelaFetch(token);
          console.warn('[useJukebox] active sync fila:', err);
        });
    }, ACTIVE_SYNC_MS);
    return () => clearInterval(id);
  }, [telaId, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch]);

  // Um único visibilitychange: ordem de reconnect importa (R4/R5/R7).
  const reconexaoEmAndamentoRef = useRef(false);
  const reconectarCanais = useCallback((evento: EventoReconexaoJukebox) => {
    if (!telaId || !deveReconectarJukebox(evento, document.visibilityState)) return;
    if (reconexaoEmAndamentoRef.current) return;

    reconexaoEmAndamentoRef.current = true;
    // init abre a janela neste tick. Cada criarCanal espera o removeChannel
    // terminar antes de abrir o tópico de novo; enquanto o anterior está em
    // `leaving` não há CDC, e o que chegar depois do subscribe entra na janela.
    criarCanalBroadcast();
    criarCanalFila();
    criarCanalPlaylist();
    void initRef.current().finally(() => {
      reconexaoEmAndamentoRef.current = false;
    });
  }, [
    telaId,
    criarCanalBroadcast,
    criarCanalFila,
    criarCanalPlaylist,
  ]);

  useEffect(() => {
    if (!telaId) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        destruirCanalBroadcast();
        destruirCanalFila();
        destruirCanalPlaylist();
        return;
      }
      reconectarCanais('visibilitychange');
    };
    const handlePageShow = () => reconectarCanais('pageshow');
    const handleOnline = () => reconectarCanais('online');
    // `focus` é o fallback de `visibilitychange`: PWA "adicionado à tela
    // inicial" no iOS e navegadores in-app (WhatsApp/Instagram, comuns em QR
    // code de mesa) às vezes não disparam visibilitychange/pageshow ao
    // retomar — sem isso, fila/cooldown só voltavam a sincronizar com F5.
    const handleFocus = () => reconectarCanais('focus');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    telaId,
    destruirCanalBroadcast,
    destruirCanalFila,
    destruirCanalPlaylist,
    reconectarCanais,
  ]);

  return {
    tela,
    tvOnline,
    sincronizando,
    items,
    filaAtiva,
    cooldowns,
    loading,
    pedindoId,
    toast,
    pedidoConfirmado,
    showPopup,
    agora,
    musicaAtual,
    pedidoIniciado,
    sugestaoAprovada,
    sugestaoRecusada,
    pdvToast,
    meuSessionId:         sessionIdRef.current,
    handlePedir,
    refresh:              () => initRef.current(),
    dismissToast,
    dismissConfirmado,
    dismissPopup,
    batalhaVibesAtiva,
    temaDiaNamorados,
    temaFestaJunina,
    temaCopa,
    pendingVibeItem,
    handlePedirComVibe,
    cancelarVibeSelection,
    meuTime,
  };
}
